import { useEffect, useRef, useState } from "react";
import {
  listTrailEvents,
  createTrailEvent,
  cancelTrailEvent,
  archiveTrailEvent,
  announceTrailEvent,
  ApiError,
  type TrailEvent,
  type EventType,
} from "../api";
import { EventImageUploader } from "./EventImageUploader";

const EVENT_ANNOUNCE_CAP = 3;

/**
 * Events section — Phase B3.
 *
 * Renders as the Events "tool" in the My Trails dashboard for
 * anyone with host_events on the trail (owner or steward).
 * Lists upcoming events with RSVP counts, lets a host create new
 * ones inline, and cancel existing ones (soft cancel — preserves
 * the row + RSVPs so a rider sees "this got called off").
 *
 * The full edit flow is a TODO for B3.1 — for now the host creates
 * fresh + cancels. Tweaks happen by cancelling + recreating, which
 * is the safer destructive-action default.
 */

const EVENT_TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: "trail_day", label: "🚵 Trail day" },
  { value: "work_party", label: "🛠️ Work party" },
  { value: "group_ride", label: "🚲 Group ride" },
  { value: "race", label: "🏁 Race" },
  { value: "other", label: "📅 Other" },
];

export function EventsSection({
  slug,
  canHost,
}: {
  slug: string;
  /** True when the signed-in user can host (host_events capability —
   *  owner or steward). Stewards see the list AND the create form
   *  the same as owners. */
  canHost: boolean;
}) {
  const [events, setEvents] = useState<TrailEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  async function reload() {
    setError(null);
    try {
      const r = await listTrailEvents(slug);
      setEvents(r.items);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't load events."
      );
      setEvents([]);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <div className="admin-section">
      <div className="admin-section-heading">Events</div>
      <div className="admin-section-body">
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          Work parties, group rides, races, trail days. Riders see these on
          the trail page and can RSVP.
        </p>

        {error && (
          <div className="admin-feedback admin-feedback-err">{error}</div>
        )}

        {events === null ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Loading events…
          </div>
        ) : events.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Nothing on the calendar yet.
          </div>
        ) : (
          <ul className="event-list" role="list">
            {events.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                canHost={canHost}
                onCancelled={reload}
                onAnnounced={reload}
              />
            ))}
          </ul>
        )}

        {canHost && !composeOpen && (
          <button
            type="button"
            className="small secondary"
            onClick={() => setComposeOpen(true)}
            style={{ alignSelf: "flex-start" }}
          >
            + Add an event
          </button>
        )}

        {canHost && composeOpen && (
          <EventCompose
            slug={slug}
            onCancel={() => setComposeOpen(false)}
            onCreated={() => {
              setComposeOpen(false);
              reload();
            }}
          />
        )}
      </div>
    </div>
  );
}

function EventRow({
  event,
  canHost,
  onCancelled,
  onAnnounced,
}: {
  event: TrailEvent;
  canHost: boolean;
  onCancelled: () => void;
  onAnnounced: () => void;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [feedback, setFeedback] = useState<
    { kind: "ok" | "err"; msg: string } | null
  >(null);

  async function handleCancel() {
    // Two-step confirm so the audience picker is explicit. Default is
    // "Everyone subscribed" (max visibility); the host can opt to
    // limit blast radius to just the riders who RSVP'd.
    const audience = chooseCancelAudience(event);
    if (audience == null) return; // user bailed at the prompt
    setCancelling(true);
    setFeedback(null);
    try {
      const r = await cancelTrailEvent(event.id, audience);
      setFeedback({
        kind: "ok",
        msg: `Cancellation pushed to ${r.pushed} rider${r.pushed === 1 ? "" : "s"}.`,
      });
      onCancelled();
    } catch (err) {
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Couldn't cancel.",
      });
    } finally {
      setCancelling(false);
    }
  }

  async function handleArchive() {
    if (
      !confirm(
        `Remove "${event.title}" from this list? We'll keep the record for analytics, but it won't appear on your trail card or in the rider app anymore.`
      )
    ) {
      return;
    }
    setArchiving(true);
    setFeedback(null);
    try {
      await archiveTrailEvent(event.id);
      // The parent list refetches on cancellation; reuse that hook —
      // the archived row will drop out on the next reload.
      onCancelled();
    } catch (err) {
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Couldn't archive.",
      });
    } finally {
      setArchiving(false);
    }
  }

  async function handleAnnounce() {
    const sequenceWord =
      event.announcementCount === 0
        ? "Announce this event"
        : "Send another update";
    if (
      !confirm(
        `${sequenceWord} to every rider with this trail's notifications on?`
      )
    )
      return;
    setAnnouncing(true);
    setFeedback(null);
    try {
      const r = await announceTrailEvent(event.id);
      const noun = r.pushed === 1 ? "rider" : "riders";
      setFeedback({
        kind: "ok",
        msg: `📣 Pushed to ${r.pushed} ${noun}. ${r.capRemaining} announces left for this event.`,
      });
      onAnnounced();
    } catch (err) {
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Couldn't announce.",
      });
    } finally {
      setAnnouncing(false);
    }
  }

  const isCancelled = event.cancelledAt != null;
  const typeOption = EVENT_TYPE_OPTIONS.find((o) => o.value === event.eventType);
  const typeLabel = typeOption?.label ?? "📅 Event";
  const [typeEmoji, ...typeWords] = typeLabel.split(" ");
  const typeName = typeWords.join(" ") || "Event";

  const announceLeft = EVENT_ANNOUNCE_CAP - event.announcementCount;
  const announceMenuLabel =
    event.announcementCount === 0
      ? "Announce to riders"
      : announceLeft <= 0
        ? "Announce cap reached"
        : `Re-announce (${announceLeft} left)`;

  const busy = announcing || cancelling || archiving;
  const rsvpTotal = event.rsvps.going + event.rsvps.maybe;

  // HIG list-row anatomy: leading media (thumbnail) → primary +
  // secondary text → trailing accessory (overflow menu). Each event is
  // its own bounded card (Law of Common Region) so it reads as a unit.
  return (
    <li className={`event-card ${isCancelled ? "is-cancelled" : ""}`}>
      <div className="event-card-body">
        <EventThumb
          imageUrl={event.image?.url ?? null}
          typeEmoji={typeEmoji}
          canHost={canHost}
          editing={showImage}
          onToggle={() => setShowImage((v) => !v)}
        />

        <div className="event-card-text">
          <div className="event-card-title">
            <span className="event-card-name">{event.title}</span>
            {isCancelled && (
              <span className="event-row-cancelled-pill">Cancelled</span>
            )}
          </div>
          <div className="event-card-meta muted">
            {typeName} · {formatEventTime(event.startsAt, event.endsAt)}
            {event.location && ` · ${event.location}`}
          </div>
          <div className="event-card-rsvp muted">
            {rsvpTotal === 0 ? (
              "No RSVPs yet"
            ) : (
              <>
                {event.rsvps.going > 0 && <>👍 {event.rsvps.going} going</>}
                {event.rsvps.maybe > 0 && (
                  <span style={{ marginLeft: event.rsvps.going > 0 ? 10 : 0 }}>
                    🤔 {event.rsvps.maybe} maybe
                  </span>
                )}
              </>
            )}
          </div>
          {event.lastAnnouncedAt != null && (
            <div className="event-card-announce">
              📣 Announced {timeAgoSeconds(event.lastAnnouncedAt)} ·{" "}
              {event.announcementCount} of {EVENT_ANNOUNCE_CAP} sent
            </div>
          )}
        </div>

        {canHost && (
          <EventActionsMenu
            isCancelled={isCancelled}
            announceLabel={announceMenuLabel}
            announceDisabled={announceLeft <= 0}
            hasImage={event.image != null}
            busy={busy}
            onAnnounce={handleAnnounce}
            onCancel={handleCancel}
            onArchive={handleArchive}
            onEditPhoto={() => setShowImage(true)}
          />
        )}
      </div>

      {canHost && showImage && (
        <div className="event-card-photo-editor">
          <EventImageUploader
            compact
            eventId={event.id}
            initialUrl={event.image?.url ?? null}
          />
        </div>
      )}

      {feedback && (
        <div
          className={
            feedback.kind === "ok"
              ? "admin-feedback admin-feedback-ok"
              : "admin-feedback admin-feedback-err"
          }
          style={{ marginTop: 10 }}
        >
          {feedback.msg}
        </div>
      )}
    </li>
  );
}

/**
 * Leading media tile — HIG list-row "image" slot. Shows the event's
 * photo when set; for hosts with no photo it's an inviting "＋ Photo"
 * drop target (the primary add affordance); for riders/no-photo it
 * falls back to the event-type emoji. 64² keeps a comfortable Fitts
 * target. Tapping toggles the inline photo editor.
 */
function EventThumb({
  imageUrl,
  typeEmoji,
  canHost,
  editing,
  onToggle,
}: {
  imageUrl: string | null;
  typeEmoji: string;
  canHost: boolean;
  editing: boolean;
  onToggle: () => void;
}) {
  const inner = imageUrl ? (
    <>
      <img src={imageUrl} alt="" className="event-thumb-img" />
      {canHost && <span className="event-thumb-edit">Edit</span>}
    </>
  ) : canHost ? (
    <span className="event-thumb-add">
      <span className="event-thumb-add-plus">＋</span>
      <span className="event-thumb-add-label">Photo</span>
    </span>
  ) : (
    <span className="event-thumb-emoji">{typeEmoji}</span>
  );

  const className = `event-thumb ${imageUrl ? "has-img" : "is-empty"} ${
    editing ? "is-editing" : ""
  }`;

  if (!canHost) {
    return <div className={className}>{inner}</div>;
  }
  return (
    <button
      type="button"
      className={className}
      onClick={onToggle}
      aria-label={imageUrl ? "Change event photo" : "Add event photo"}
      aria-pressed={editing}
    >
      {inner}
    </button>
  );
}

/**
 * Trailing overflow menu — collapses the old Announce/Cancel/Archive
 * button pile into one accessory (Hick's law: fewer visible choices,
 * faster scan). Standard menu affordances: click-outside + Escape to
 * dismiss, roving focus targets. Destructive Archive is set apart
 * below a separator and tinted.
 */
function EventActionsMenu({
  isCancelled,
  announceLabel,
  announceDisabled,
  hasImage,
  busy,
  onAnnounce,
  onCancel,
  onArchive,
  onEditPhoto,
}: {
  isCancelled: boolean;
  announceLabel: string;
  announceDisabled: boolean;
  hasImage: boolean;
  busy: boolean;
  onAnnounce: () => void;
  onCancel: () => void;
  onArchive: () => void;
  onEditPhoto: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function run(fn: () => void) {
    setOpen(false);
    fn();
  }

  return (
    <div className="event-menu" ref={ref}>
      <button
        type="button"
        className="event-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Event actions"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
      >
        {busy ? "…" : "⋯"}
      </button>
      {open && (
        <div className="event-menu-pop" role="menu">
          <button
            type="button"
            role="menuitem"
            className="event-menu-item"
            onClick={() => run(onEditPhoto)}
          >
            <span className="event-menu-icon">🖼</span>
            {hasImage ? "Change photo" : "Add photo"}
          </button>
          {!isCancelled && (
            <>
              <div className="event-menu-sep" />
              <button
                type="button"
                role="menuitem"
                className="event-menu-item"
                onClick={() => run(onAnnounce)}
                disabled={announceDisabled}
              >
                <span className="event-menu-icon">📣</span>
                {announceLabel}
              </button>
              <button
                type="button"
                role="menuitem"
                className="event-menu-item"
                onClick={() => run(onCancel)}
              >
                <span className="event-menu-icon">🚫</span>
                Cancel event
              </button>
            </>
          )}
          <div className="event-menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="event-menu-item danger"
            onClick={() => run(onArchive)}
          >
            <span className="event-menu-icon">🗑</span>
            Archive
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Two-step cancel confirm with an explicit audience picker. Returns
 * the chosen audience or null if the host bailed at either prompt.
 * Default is "all" (max visibility), matching the design call we
 * landed on with Eric — cancellation is about awareness.
 */
function chooseCancelAudience(
  event: TrailEvent
): "all" | "rsvps_only" | null {
  const rsvpCount = event.rsvps.going + event.rsvps.maybe;
  if (
    !confirm(
      `Cancel "${event.title}"? Riders need to know — by default we'll push to everyone subscribed to this trail.`
    )
  ) {
    return null;
  }
  // Only surface the audience picker when there's an RSVP'd subset
  // worth picking. With zero RSVPs the "only RSVP'd" branch would
  // send to nobody, so it'd be a worse choice than the default.
  if (rsvpCount === 0) return "all";
  const onlyRsvps = confirm(
    `Send to only the ${rsvpCount} rider${rsvpCount === 1 ? "" : "s"} who RSVP'd?\n\n• OK  = only RSVP'd attendees\n• Cancel = everyone subscribed (default)`
  );
  return onlyRsvps ? "rsvps_only" : "all";
}

function timeAgoSeconds(unixSeconds: number): string {
  const delta = Math.max(0, Math.floor(Date.now() / 1000) - unixSeconds);
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86_400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86_400)}d ago`;
}

function EventCompose({
  slug,
  onCancel,
  onCreated,
}: {
  slug: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventType, setEventType] = useState<EventType>("trail_day");
  const [startsAtLocal, setStartsAtLocal] = useState<string>(defaultLocalDateTime());
  const [endsAtLocal, setEndsAtLocal] = useState<string>("");
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    if (!title.trim()) return setError("Title is required.");
    const startsAt = localDateTimeToUnix(startsAtLocal);
    if (!startsAt) return setError("Start time is required.");
    const endsAt = endsAtLocal
      ? localDateTimeToUnix(endsAtLocal) ?? undefined
      : undefined;
    if (endsAt && endsAt <= startsAt) {
      return setError("End time must be after start.");
    }
    setSubmitting(true);
    try {
      await createTrailEvent(slug, {
        title: title.trim(),
        description: description.trim() || undefined,
        eventType,
        startsAt,
        endsAt,
        location: location.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't create the event."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="event-compose">
      <div className="event-compose-heading">New event</div>

      <label className="drawer-field">
        <span className="drawer-field-label">What's it about?</span>
        <input
          type="text"
          value={title}
          placeholder="e.g. Spring trail day"
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          autoFocus
        />
      </label>

      <label className="drawer-field">
        <span className="drawer-field-label">Type</span>
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value as EventType)}
        >
          {EVENT_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <div className="event-compose-time">
        <label className="drawer-field">
          <span className="drawer-field-label">Starts</span>
          <input
            type="datetime-local"
            value={startsAtLocal}
            onChange={(e) => setStartsAtLocal(e.target.value)}
          />
        </label>
        <label className="drawer-field">
          <span className="drawer-field-label">Ends (optional)</span>
          <input
            type="datetime-local"
            value={endsAtLocal}
            onChange={(e) => setEndsAtLocal(e.target.value)}
          />
        </label>
      </div>

      <label className="drawer-field">
        <span className="drawer-field-label">Where (optional)</span>
        <input
          type="text"
          value={location}
          placeholder="Defaults to the trailhead"
          onChange={(e) => setLocation(e.target.value)}
        />
      </label>

      <label className="drawer-field">
        <span className="drawer-field-label">Details (optional)</span>
        <textarea
          rows={3}
          value={description}
          placeholder="What to bring, what to expect, who to contact."
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
        />
      </label>

      {error && (
        <div className="admin-feedback admin-feedback-err">{error}</div>
      )}

      <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="button" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving…" : "Save event"}
        </button>
      </div>
    </div>
  );
}

function defaultLocalDateTime(): string {
  const d = new Date();
  // Default to next round half-hour about a week from now — gives a
  // sensible starting point so the host doesn't stare at "right now".
  d.setDate(d.getDate() + 7);
  d.setHours(10, 0, 0, 0);
  return toLocalDateTimeInputValue(d);
}

function toLocalDateTimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localDateTimeToUnix(s: string): number | null {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / 1000);
}

function formatEventTime(startsAt: number, endsAt: number | null): string {
  const start = new Date(startsAt * 1000);
  const end = endsAt ? new Date(endsAt * 1000) : null;
  const startDate = start.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const startTime = start.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  if (!end) return `${startDate} · ${startTime}`;
  // Same-day range collapses to "Sat, Jun 14 · 9:00 AM – 2:00 PM".
  const sameDay =
    start.toDateString() === end.toDateString();
  const endTime = end.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay) return `${startDate} · ${startTime} – ${endTime}`;
  const endDate = end.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${startDate} ${startTime} – ${endDate} ${endTime}`;
}
